import type {
  DataNormalization,
  DataTransformation,
} from '../../../PageConfigProvider/types/item';
import type {
  MetricValues,
  PayMetricValues,
  PostingMetricValues,
  ScreenerMetricValues,
  SentimentMetricValues,
  TransitionMetricValues,
} from '../../../PageConfigProvider/types/metric';

export type ColorScheme = 'sequential' | 'diverging'; // 'categorical'
export type ColorSchemeDirection = 'positive' | 'negative';

export type ScaleConfig = {
  type: ColorScheme;
  direction: ColorSchemeDirection;
  domain?: { min?: number; max?: number; center?: number }; // for diverging schemes only
  normalization?: DataNormalization;
  transformation?: DataTransformation;
};

const makeSequential = (
  direction: ColorSchemeDirection = 'positive',
  domain?: { min?: number; max?: number },
  normalization: DataNormalization = 'none',
  transformation: DataTransformation = 'none'
): ScaleConfig => ({
  type: 'sequential',
  direction,
  domain,
  normalization,
  transformation,
});

const makeDiverging = (
  domain: { min?: number; max?: number; center?: number },
  direction: ColorSchemeDirection = 'positive',
  normalization: DataNormalization = 'none',
  transformation: DataTransformation = 'none'
): ScaleConfig => {
  return {
    type: 'diverging' as const,
    direction,
    domain,
    normalization,
    transformation,
  };
};

const compositionSequential: Record<
  'headcount' | 'salary_growth',
  ScaleConfig
> = {
  headcount: makeSequential('positive', undefined, 'area_density', 'sqrt'),
  salary_growth: makeDiverging({ center: 0 }),
  // salary: makeSequential(),
  // tenure: makeSequential(),
  // growth_rate: makeDiverging({
  //   min: -0.2,
  //   max: 0.2,
  //   center: 0,
  // }),
  // hiring_rate: makeSequential(),
  // attrition_rate: makeSequential('negative'),
};

const screenerConfigs: Record<ScreenerMetricValues, ScaleConfig> = {
  screener_headcount: makeSequential(),
  screener_salary: makeSequential(),
  screener_tenure: makeSequential(),
  screener_growth: makeDiverging({
    min: -0.2,
    max: 0.2,
    center: 0,
  }),
  screener_growth_yoy: makeDiverging({
    min: -0.2,
    max: 0.2,
    center: 0,
  }),

  screener_hiring: makeSequential('positive', {
    min: 0,
    max: 1,
  }),
  screener_attrition: makeSequential('negative', {
    min: 0,
    max: 1,
  }),
  screener_inflow: makeSequential(),
  screener_outflow: makeSequential(),
};

const postingConfigs: Record<PostingMetricValues, ScaleConfig> = {
  posting_salary: makeSequential(),
  time_to_fill: makeSequential('negative'),
  postings_active: makeSequential(),
  postings_new: makeSequential(),
  postings_removed: makeSequential('negative'),
  postings_volume: makeSequential(),
  time_to_fill_percentile: makeSequential('negative'),
};

const payConfigs: Record<PayMetricValues, ScaleConfig> = {
  total_compensation: makeSequential(),
  base_salary: makeSequential(),
  contractor_pay_rate: makeSequential(),
  total_spend_salary: makeSequential(),
  total_spend_compensation: makeSequential(),
  base_salary_percentiles: makeSequential(),
  total_compensation_percentiles: makeSequential(),
  contractor_pay_rate_percentiles: makeSequential(),
};

const transitionConfigs: Record<TransitionMetricValues, ScaleConfig> = {
  transitions_inflow: makeSequential(),
  transitions_outflow: makeSequential('negative'),
};

const sentimentConfigs: Record<SentimentMetricValues, ScaleConfig> = {
  sentiment_top_bottom: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  overall: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  business_outlook: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  career_growth: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  compensation: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  benefits: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  healthcare: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  retirement_pension: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  childcare: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  education_assistance: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  wellness_programs: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  work_life_balance: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  diversity_and_inclusion: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  leadership: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  culture: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  remote_work: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  onboarding: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  mentorship: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  technology: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  promotion_opportunities: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  paid_time_off: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  transparency: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  efficiency_and_bureaucracy: makeDiverging({
    min: 0.2,
    max: 0.8,
    center: 0.5,
  }),
  product_and_service_quality: makeDiverging({
    min: 0.2,
    max: 0.8,
    center: 0.5,
  }),
  prestige: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  coworkers: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  mission_and_vision: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  workplace_facilities: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  training: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  clients_and_customers: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  company_growth: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  schedule_flexibility: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  job_security: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  overtime: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  family_support: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  commission: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  independence: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  commute: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  safety: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  internal_communication: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  office_politics: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  harassment: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  honesty: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  ethics: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  commitment: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  compliance: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
  integrity: makeDiverging({ min: 0.2, max: 0.8, center: 0.5 }),
};

const defaultConfig: ScaleConfig = makeSequential(
  'positive',
  undefined,
  'none',
  'none'
);

export const getMetricPaletteConfig = (metric?: MetricValues): ScaleConfig => {
  if (!metric) return defaultConfig;

  return (
    (compositionSequential as Record<string, ScaleConfig>)[metric] ||
    (screenerConfigs as Record<string, ScaleConfig>)[metric] ||
    (postingConfigs as Record<string, ScaleConfig>)[metric] ||
    (payConfigs as Record<string, ScaleConfig>)[metric] ||
    (transitionConfigs as Record<string, ScaleConfig>)[metric] ||
    (sentimentConfigs as Record<string, ScaleConfig>)[metric] ||
    defaultConfig
  );
};
